import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../lib/queryKeys';
import {
  tagsService,
  type Tag,
  type TagFilters,
  type TagCreateInput,
  type TagUpdateInput,
} from '../services/tags.service';
import {
  createOptimisticItem,
  onMutateCreate,
  onMutateDelete,
  onError,
  onSuccessCreate,
  onSettled,
} from '../lib/optimisticHelpers';

// Re-export types for backwards compatibility
export type { Tag, TagFilters, TagCreateInput, TagUpdateInput };

/**
 * Result interface for useTags hook
 * Maintains backwards compatibility with existing implementation
 */
interface UseTagsResult {
  tags: Tag[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  createTag: (data: TagCreateInput) => Promise<void>;
  updateTag: (id: string, data: TagUpdateInput) => Promise<void>;
  deleteTag: (id: string) => Promise<void>;
  mergeTag: (sourceTagIds: string[], targetTagId: string) => Promise<void>;
  bulkCreateTags: (tags: TagCreateInput[]) => Promise<void>;
}

/**
 * Custom hook for managing tags with React Query
 *
 * Benefits over previous implementation:
 * - Automatic request deduplication
 * - Intelligent caching (5 min stale time, 10 min cache time)
 * - Automatic background refetching
 * - Better error handling and retry logic
 * - DevTools for debugging queries
 *
 * React Query automatically handles:
 * - Preventing duplicate concurrent requests
 * - Caching responses based on filter parameters
 * - Background refetching when data becomes stale
 * - Request cancellation
 * - Error retry with exponential backoff
 *
 * @param filters - Optional filters for tags (category, search, active, lang)
 */
export const useTags = (filters?: TagFilters): UseTagsResult => {
  const queryClient = useQueryClient();

  // Main query for fetching tags
  // React Query will cache results based on filter parameters
  const {
    data: tags = [],
    isLoading: loading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.tags.list(filters),
    queryFn: () => tagsService.getAll(filters),
    // Additional options (defaults from queryClient are already optimal)
    staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  });

  // Convert query error to string for backwards compatibility
  const error = queryError ? (queryError as Error).message : null;

  // Tag mutations with optimistic updates
  const createTagMutation = useMutation({
    mutationFn: tagsService.create,
    onMutate: async (newTag) => {
      const optimisticTag = createOptimisticItem<Tag>({
        ...newTag,
        usageCount: 0,
        isActive: true,
      });
      return onMutateCreate(
        queryClient,
        queryKeys.tags.list(filters),
        optimisticTag
      );
    },
    onError: (_err, _variables, context) => {
      onError(queryClient, queryKeys.tags.list(filters), context);
    },
    onSuccess: (data) => {
      onSuccessCreate(queryClient, queryKeys.tags.list(filters), data);
    },
    onSettled: () => {
      onSettled(queryClient, queryKeys.tags.all);
    },
  });

  const updateTagMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: TagUpdateInput }) =>
      tagsService.update(id, data),
    onMutate: async ({ id, data }) => {
      const queryKey = queryKeys.tags.list(filters);
      await queryClient.cancelQueries({
        queryKey: queryKey as unknown as unknown[],
      });

      const previousData = queryClient.getQueryData<Tag[]>(
        queryKey as unknown as unknown[]
      );

      // Update the tag optimistically
      queryClient.setQueryData<Tag[]>(
        queryKey as unknown as unknown[],
        (old = []) =>
          old.map((tag) =>
            tag.id === id
              ? {
                  ...tag,
                  ...data,
                  updatedAt: new Date().toISOString(),
                }
              : tag
          )
      );

      return { previousData };
    },
    onError: (_err, _variables, context) => {
      onError(queryClient, queryKeys.tags.list(filters), context);
    },
    onSettled: () => {
      onSettled(queryClient, queryKeys.tags.all);
    },
  });

  const deleteTagMutation = useMutation({
    mutationFn: tagsService.delete,
    onMutate: async (id) => {
      return onMutateDelete(queryClient, queryKeys.tags.list(filters), id);
    },
    onError: (_err, _variables, context) => {
      onError(queryClient, queryKeys.tags.list(filters), context);
    },
    onSettled: () => {
      onSettled(queryClient, queryKeys.tags.all);
    },
  });

  const mergeTagMutation = useMutation({
    mutationFn: ({
      sourceTagIds,
      targetTagId,
    }: {
      sourceTagIds: string[];
      targetTagId: string;
    }) => tagsService.merge(sourceTagIds, targetTagId),
    onMutate: async ({ sourceTagIds, targetTagId }) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.tags.list(filters),
      });

      const previousData = queryClient.getQueryData<Tag[]>(
        queryKeys.tags.list(filters)
      );

      // Optimistically remove source tags and update target tag usage count
      queryClient.setQueryData<Tag[]>(
        queryKeys.tags.list(filters),
        (old = []) => {
          const sourceTags = old.filter((tag) => sourceTagIds.includes(tag.id));
          const sourceUsageCount = sourceTags.reduce(
            (sum, tag) => sum + (tag.usageCount || 0),
            0
          );

          return old
            .filter((tag) => !sourceTagIds.includes(tag.id))
            .map((tag) =>
              tag.id === targetTagId
                ? {
                    ...tag,
                    usageCount: (tag.usageCount || 0) + sourceUsageCount,
                    updatedAt: new Date().toISOString(),
                  }
                : tag
            );
        }
      );

      return { previousData };
    },
    onError: (_err, _variables, context) => {
      onError(queryClient, queryKeys.tags.list(filters), context);
    },
    onSettled: () => {
      onSettled(queryClient, queryKeys.tags.all);
    },
  });

  const bulkCreateTagsMutation = useMutation({
    mutationFn: tagsService.bulkCreate,
    onMutate: async (newTags) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.tags.list(filters),
      });

      const previousData = queryClient.getQueryData<Tag[]>(
        queryKeys.tags.list(filters)
      );

      // Create multiple optimistic tags
      const optimisticTags = newTags.map((tag) =>
        createOptimisticItem<Tag>({
          ...tag,
          usageCount: 0,
          isActive: true,
        })
      );

      // Add all optimistic tags to the cache
      queryClient.setQueryData<Tag[]>(
        queryKeys.tags.list(filters),
        (old = []) => [...old, ...optimisticTags]
      );

      return { previousData };
    },
    onError: (_err, _variables, context) => {
      onError(queryClient, queryKeys.tags.list(filters), context);
    },
    onSuccess: (data) => {
      // Replace all optimistic tags with real data
      queryClient.setQueryData<Tag[]>(
        queryKeys.tags.list(filters),
        (old = []) => {
          // Remove all temp tags
          const withoutTemp = old.filter((tag) => !tag.id.startsWith('temp-'));
          // Add real tags from server
          return [...withoutTemp, ...data];
        }
      );
    },
    onSettled: () => {
      onSettled(queryClient, queryKeys.tags.all);
    },
  });

  // Wrapper functions to maintain backwards compatibility
  const createTag = async (data: TagCreateInput) => {
    await createTagMutation.mutateAsync(data);
  };

  const updateTag = async (id: string, data: TagUpdateInput) => {
    await updateTagMutation.mutateAsync({ id, data });
  };

  const deleteTag = async (id: string) => {
    await deleteTagMutation.mutateAsync(id);
  };

  const mergeTag = async (sourceTagIds: string[], targetTagId: string) => {
    await mergeTagMutation.mutateAsync({ sourceTagIds, targetTagId });
  };

  const bulkCreateTags = async (tags: TagCreateInput[]) => {
    await bulkCreateTagsMutation.mutateAsync(tags);
  };

  // Wrapper for refetch to match old signature
  const refetchWrapper = async () => {
    await refetch();
  };

  return {
    tags,
    loading,
    error,
    refetch: refetchWrapper,
    createTag,
    updateTag,
    deleteTag,
    mergeTag,
    bulkCreateTags,
  };
};
